import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, useLoaderData, useLocation, useNavigation } from 'react-router';

import { useIsMobile } from '~/core/hooks/useMediaQuery';
import { AppRouteKey } from '~/core/router/route_types';
import { AppRoutes } from '~/core/router/routes';
import { SidebarType } from '~/core/types/sidebar_types';
import { SidebarPropsModel } from '~/datasources/sidebar_datasource/models/sidebar_props';
import { loader } from '~/root';

import { Modal } from '../common/Modal';
import { CustomButton } from '../components/custom_button';
import CustomDropdown from '../components/custom_dropdown';
import { StepLoader } from '../components/spinner_loader';
import { useLayoutStore } from '../stores/layoutStore';

import { getIconByName } from './sidebar_icon_mapper';
import {
  getTranslatedLabel,
  handleOrganizationChange,
  isRouteActive,
  useSidebarState,
} from './subComponents/helper';
import { OrganizationLogo } from './subComponents/organizationLogo';
import { PersonaRoleBadge } from './subComponents/personaRoleBadgeProps';
import { TabGroupItem } from './subComponents/tabGroupItemProps';
import { TabLink } from './subComponents/tabLink';
import { UserProfileSection } from './subComponents/userProfileSection';

export function Sidebar({ groups, children }: SidebarPropsModel & { children?: React.ReactNode }) {
  const location = useLocation();
  const loaderData = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation([
    'userManagement',
    'auth',
    'common',
    'home',
    'orgManagement',
    'rolesManagement',
    'audit',
    'productionHead',
    'agronomist',
    'farmerReport',
    'userReport',
  ]);

  const { isSidebarOpen, toggleSidebar, setIsLoggingOut, isLogoutModalOpen, setIsLogoutModalOpen } =
    useLayoutStore();
  const isMobile = useIsMobile();
  const navigation = useNavigation();

  const isLoggingOut =
    (navigation.state === 'submitting' || navigation.state === 'loading') &&
    navigation.formData?.get('actionType') === 'logout';

  // Mobile drawer animation state
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [mobileDrawerOnScreen, setMobileDrawerOnScreen] = useState(false);

  const sidebarState = useSidebarState();
  const {
    isDrawerOpen,
    setIsDrawerOpen,
    // isSidebarOpen,
    // setIsSidebarOpen,
    collapsedGroups,
    activeTabGroupId,
    activeTabGroupItemId,
    toggleGroup,
    handleTabGroupItemClick,
  } = sidebarState;

  // Handle mobile drawer mount/unmount with animation
  useEffect(() => {
    if (isDrawerOpen) {
      setMobileDrawerVisible(true);
    } else {
      setMobileDrawerOnScreen(false);
      const tid = setTimeout(() => setMobileDrawerVisible(false), 700);
      return () => clearTimeout(tid);
    }
  }, [isDrawerOpen]);

  // Force browser to paint off-screen before sliding in
  useLayoutEffect(() => {
    if (mobileDrawerVisible && isDrawerOpen) {
      setMobileDrawerOnScreen(false);
      const raf = requestAnimationFrame(() => {
        setMobileDrawerOnScreen(true);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [mobileDrawerVisible, isDrawerOpen]);

  const firstName = loaderData.userMetadata?.firstName || 'USER';
  const currentLoggedInUser = loaderData.userMetadata;

  // Auto-close drawer on mobile when navigating
  const handleMobileNavigate = () => {
    if (isMobile && isDrawerOpen) {
      setIsDrawerOpen(false);
    }
  };

  const currentOrganization =
    currentLoggedInUser?.organizations?.find((org) => org.isCurrent) ||
    currentLoggedInUser?.organizations?.[0] ||
    null;
  const organizations = loaderData.userMetadata?.organizations;

  const platformRole = currentOrganization?.platformRole?.platformRoleName;
  const personaRole = currentOrganization?.personaRoles?.find(
    (role) => role.isCurrent
  )?.personaRoleNameCaption;

  const getLabel = (key: string) => getTranslatedLabel(key, i18n, t);

  const handleOpenLogoutModal = () => setIsLogoutModalOpen(true);
  const handleCloseLogoutModal = () => setIsLogoutModalOpen(false);
  const handleConfirmLogout = () => {
    setIsLoggingOut(true);
  };

  const renderTab = (tab: any, isTopLevel = false) => {
    if (!tab.route || !tab.route.key) return null;
    const routePath = AppRoutes[tab.route.key as AppRouteKey]?.path;
    if (!routePath) return null;

    const isActive = isRouteActive(location, routePath, tab.route.key);

    return (
      <div key={tab.id}>
        <TabLink
          tab={tab}
          routePath={routePath}
          isActive={isActive}
          isSidebarOpen={isSidebarOpen}
          getLabel={getLabel}
          onMobileNavigate={handleMobileNavigate}
        />
      </div>
    );
  };

  const renderTabGroupItem = (item: any, groupId: string) => {
    const isActive =
      location.pathname === item.route?.path ||
      (activeTabGroupItemId === item.id && activeTabGroupId === groupId);

    return (
      <div key={item.id}>
        <TabGroupItem
          item={item}
          groupId={groupId}
          isActive={isActive}
          isSidebarOpen={isSidebarOpen}
          getLabel={getLabel}
          onClick={handleTabGroupItemClick}
          onMobileNavigate={handleMobileNavigate}
        />
      </div>
    );
  };

  const renderTabGroup = (group: any) => {
    const isCollapsed = isSidebarOpen
      ? (collapsedGroups[group.id] ?? group.defaultCollapsed)
      : false;
    const isGroupActive = group.items?.some((item: any) =>
      location.pathname.includes(item.route?.path)
    );

    return (
      <div
        key={group.id}
        className="mb-2"
      >
        <div
          className={`flex h-12 items-center px-5 py-2 focus:outline-none md:px-10 ${isGroupActive ? 'bg-primaryDark' : 'bg-transparent'}`}
          role="button"
          tabIndex={0}
          aria-expanded={!isCollapsed}
          onClick={() => toggleGroup(group.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleGroup(group.id);
            }
          }}
        >
          {group.icon && (
            <span
              className={`mr-3 ${isGroupActive ? 'text-primaryDefault' : 'text-neutralDefault'}`}
            >
              {getIconByName(group.icon)}
            </span>
          )}
          <span className="text-white">{getLabel(group.item_caption_ui)}</span>
        </div>
        {!isCollapsed && group.items && (
          <div className="flex flex-col gap-1 pb-2">
            {group.items.map((item: any) =>
              item.type === SidebarType.TABGROUPITEM ? renderTabGroupItem(item, group.id) : null
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (group: any, isTopLevel = true) => {
    if (group.type === SidebarType.TAB) return renderTab(group, isTopLevel);
    if (group.type === SidebarType.TABGROUP) return renderTabGroup(group);

    const isCollapsed = isSidebarOpen
      ? (collapsedGroups[group.id] ?? group.defaultCollapsed)
      : false;

    return (
      <div
        key={group.id}
        className="mb-4"
      >
        {isSidebarOpen && (
          <div
            className="flex cursor-pointer items-center px-5 py-2 transition-all duration-300 ease-in-out hover:opacity-80 focus:outline-none"
            role="button"
            tabIndex={0}
            aria-expanded={!isCollapsed}
            onClick={() => toggleGroup(group.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleGroup(group.id);
              }
            }}
          >
            {group.icon ? (
              // Opt-in emphasized header: only groups that set an `icon` in their sidebar config
              // (none did before) get the icon + larger label; all other groups are unchanged
              <div className="flex w-full items-center gap-3 md:pl-5">
                <span className="flex-shrink-0 text-primaryExtraLight">
                  {getIconByName(group.icon)}
                </span>
                <span
                  className="flex-1 whitespace-nowrap text-base font-semibold text-white transition-opacity duration-300 ease-in-out"
                  style={{ opacity: isSidebarOpen ? 1 : 0 }}
                >
                  {getLabel(group.item_caption_ui)}
                </span>
                <div
                  className={`flex-shrink-0 transform transition-transform duration-300 ease-in-out ${isCollapsed ? 'rotate-0' : '-rotate-180'}`}
                >
                  <ChevronDownIcon className="h-5 w-5 text-primaryExtraLight" />
                </div>
              </div>
            ) : (
              <div className="inline-flex items-center gap-1">
                <span
                  className="whitespace-nowrap text-xs font-semibold uppercase text-primaryExtraLight transition-opacity duration-300 ease-in-out"
                  style={{ opacity: isSidebarOpen ? 1 : 0 }}
                >
                  {getLabel(group.item_caption_ui)}
                </span>
                <div
                  className={`flex-shrink-0 transform transition-transform duration-300 ease-in-out ${isCollapsed ? 'rotate-0' : '-rotate-180'}`}
                >
                  <ChevronDownIcon className="h-5 w-5 text-primaryExtraLight" />
                </div>
              </div>
            )}
          </div>
        )}

        <div
          style={{ maxHeight: isCollapsed ? 0 : undefined }}
          className={`overflow-hidden transition-all duration-500 ease-in-out opacity-${isCollapsed ? '0' : '100'}`}
        >
          <div
            ref={(el) => {
              if (!el) return;
              if (!isCollapsed) el.style.maxHeight = el.scrollHeight + 'px';
              else el.style.maxHeight = '0px';
            }}
            className="transition-max-height mt-2 w-full space-y-1 duration-500 ease-in-out"
          >
            {group.items &&
              group.items.map((item: any) => {
                if (item.type === SidebarType.GROUP) return renderGroup(item, false);
                if (item.type === SidebarType.TABGROUP) return renderTabGroup(item);
                return renderTab(item, false);
              })}
          </div>
        </div>
      </div>
    );
  };

  const sidebarContent = (
    <div className="relative z-10 flex h-full w-full flex-col justify-between overflow-hidden">
      <div>
        <div className="p-2 sm:p-2 md:p-4"></div>
        <div
          className={`h-14 border-b-[1px] border-primaryUltraDark px-2 pt-1 ${isSidebarOpen ? '' : 'h-20'}`}
        >
          {organizations && organizations.length > 0 && (
            <div
              className={`flex items-center transition-all duration-700 ease-in-out ${isSidebarOpen ? 'justify-start gap-3' : 'justify-center gap-0'}`}
            >
              <div
                className={`flex items-center transition-all duration-700 ease-in-out ${isSidebarOpen ? 'h-9 min-w-9 max-w-9' : 'h-12 min-w-12 max-w-12 justify-center'}`}
              >
                <OrganizationLogo
                  logo={currentOrganization?.organizationLogo}
                  orgName={currentOrganization?.name || ''}
                  isSidebarOpen={isSidebarOpen}
                />
              </div>

              <div
                className={`flex-1 transition-all duration-300 ease-in-out ${
                  isSidebarOpen ? 'max-w-full opacity-100' : 'max-w-0 opacity-0'
                }`}
              >
                {isSidebarOpen && (
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2"></div>
                    <CustomDropdown
                      label=""
                      options={organizations.map((org) => ({
                        label: org.name || '',
                        value: org.id || '',
                        icon: org.organizationLogo ? getIconByName(org.organizationLogo) : null,
                      }))}
                      value={currentOrganization?.id || ''}
                      onChange={(value) =>
                        handleOrganizationChange(
                          String(value),
                          organizations,
                          currentOrganization,
                          loaderData.userMetadata?.email || ''
                        )
                      }
                      variant="filled"
                      width="w-full !border-none"
                      className="mx-auto !mb-0 rounded-md border-b-2 font-semibold"
                      buttonClassName="pl-0"
                      noBorder
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-1 flex-col">
          <PersonaRoleBadge
            role={personaRole || platformRole}
            isSidebarOpen={isSidebarOpen}
          />
          <div className="flex flex-col space-y-2">{groups.map((item) => renderGroup(item))}</div>
        </div>
      </div>

      <div>
        <div className="border-t border-primaryUltraDark"></div>
        <div className="py-2"></div>
        <UserProfileSection
          firstName={firstName}
          profileImageUrl={loaderData?.userMetadata?.profileImageUrl}
          isSidebarOpen={isSidebarOpen}
          onLogoutClick={handleOpenLogoutModal}
          profilePath={AppRoutes?.[AppRouteKey.PROFILE]?.path || '/profile'}
          onMobileNavigate={handleMobileNavigate}
        />
      </div>
    </div>
  );

  return (
    <div className="side-nav flex h-screen w-full">
      <CustomButton
        variant="surface"
        buttonType="text"
        onClick={() => setIsDrawerOpen(true)}
        className="hamburger fixed left-4 top-5 z-50 flex h-10 w-10 items-center justify-center rounded-md md:hidden"
        aria-label="Open Menu"
      >
        <svg
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </CustomButton>

      <div
        className={`fixed z-10 h-full bg-primarySemiDark transition-all duration-700 ease-in-out ${isSidebarOpen ? 'w-80' : 'w-24'} hidden -translate-x-full flex-col md:relative md:flex md:translate-x-0`}
      >
        {sidebarContent}
        <CustomButton
          variant="surface"
          buttonType="text"
          // onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          onClick={toggleSidebar}
          className={`absolute right-[-20px] z-20 flex h-10 w-10 items-center justify-center rounded-full border border-primaryDark bg-primarySemiDark shadow-md transition-all duration-300 ${isSidebarOpen ? 'bottom-[4.5rem]' : 'bottom-[9rem]'}`}
          aria-label="Toggle Sidebar"
        >
          <svg
            className={`h-6 w-6 transform text-primaryDefault transition-transform duration-300 ${
              isSidebarOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </CustomButton>
      </div>

      {mobileDrawerVisible && (
        <div className="mobile-drawer fixed inset-0 z-[998] flex md:hidden">
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black transition-opacity duration-700 ease-in-out ${
              mobileDrawerOnScreen ? 'opacity-50' : 'opacity-0'
            }`}
            onClick={() => setIsDrawerOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer */}
          <div
            className={`relative z-[999] flex w-10/12 flex-col bg-primarySemiDark shadow-2xl transition-transform duration-700 ease-in-out ${
              mobileDrawerOnScreen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {/* Close button */}
            {/* <button
              onClick={() => setIsDrawerOpen(false)}
              className="bg-black/50 absolute right-3 top-3 z-[70] flex h-10 w-10 items-center justify-center rounded-full bg-primaryDark/50 text-white transition-colors hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-primaryDefault"
              aria-label="Close menu"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button> */}
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Main content */}
      <div
        id="main-content"
        className="main-wrapper h-full flex-1 overflow-y-scroll"
      >
        {children}
      </div>

      <Modal
        isOpen={isLogoutModalOpen}
        onClose={handleCloseLogoutModal}
        title={t('confirmLogoutTitle')}
        size="lg"
        className="max-sm:h-screen"
        contentWrapperClassName="p-6 flex flex-col my-auto"
        showCloseButton={false}
      >
        {isLoggingOut ? (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="relative mb-6 flex h-16 w-16 items-center justify-center">
              <div className="bg-primaryDefault/20 absolute h-16 w-16 animate-ping rounded-full" />
              <StepLoader dotCount={5} />
            </div>
            <p className="text-lg font-semibold text-primaryDark">{t('loggingOut')}</p>
          </div>
        ) : (
          <>
            <p className="mb-6 text-neutralDefault">{t('confirmLogoutMessage')}</p>
            <div className="flex justify-end gap-3">
              <CustomButton
                variant="surface"
                buttonType="text"
                className="rounded-xl border border-neutralLight px-6 transition-all duration-300 ease-in-out hover:bg-neutralExtraLight"
                onClick={handleCloseLogoutModal}
              >
                {t('cancel')}
              </CustomButton>
              <Form
                method="post"
                id="logoutForm"
                className="ml-2"
              >
                <CustomButton
                  variant="primary"
                  className="rounded-xl bg-btnColor px-6 transition-all duration-300 ease-in-out hover:bg-[#b61f1f]"
                  type="submit"
                  name="actionType"
                  value="logout"
                  isLoading={isLoggingOut}
                  onClick={handleConfirmLogout}
                >
                  {t('confirm')}
                </CustomButton>
              </Form>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
